import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Avatar, Box, CircularProgress, Divider, Popover, TextField, Typography } from '@mui/material';
import { useZoom } from 'context/ZoomContext';
import { getInitials } from 'utilities';
import { PersonSearchPopoverProps } from './PersonSearchPopover.props';

const PersonSearchPopover: React.FC<PersonSearchPopoverProps> = ({
  open,
  anchorEl,
  popoverId,
  onClose,
  searchTerm,
  onSearchTermChange,
  searching,
  results,
  currentSelections,
  onToggleSelection,
  placeholder,
  minWidth = 300,
}) => {
  const { zoomLevel } = useZoom();

  const selectedIds = new Set(currentSelections.map(s => s.targetRecordId));
  // Only show in results section items not already shown in the selected section
  const unselectedResults = results.filter(r => !selectedIds.has(r.targetRecordId));

  const renderRow = (item: ReturnType<typeof results[0]>, isSelected: boolean) => (
    <Box
      key={item.targetRecordId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 0.75,
        py: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={() => onToggleSelection(item)}>
      <Avatar alt={item.name} src={item.imageSrc} sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
        {getInitials({ name: item.name })}
      </Avatar>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {item.name}
      </Typography>
      {isSelected && <CheckIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />}
    </Box>
  );

  return (
    <Popover
      id={popoverId}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
      <Box sx={{ p: 1.5, minWidth, zoom: String(zoomLevel) }}>
        <TextField
          fullWidth
          autoFocus
          placeholder={placeholder}
          value={searchTerm}
          onChange={e => onSearchTermChange(e.target.value)}
          size="small"
        />

        {currentSelections.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {currentSelections.map(item => renderRow(item, true))}
          </Box>
        )}

        {searching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!searching && unselectedResults.length > 0 && (
          <>
            {currentSelections.length > 0 && <Divider sx={{ my: 0.75 }} />}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {unselectedResults.map(item => renderRow(item, false))}
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
};

export default PersonSearchPopover;
